import { z } from 'zod';
import { complianceRuleSchema } from '../requirements/compliance.js';
import { gateSchema, requirementKeySchema, requirementKindSchema, requirementOwnerSchema, requirementPhaseSchema, requirementStatusSchema, applicabilitySchema, } from '../requirements/index.js';
import { personIdSchema } from '../people/person.js';
import { readinessStateSchema, reviewCauseSchema } from './vocabulary.js';
import { checklistTimings } from './timing.js';
/**
 * The wire contract for a person's checklist — ONE schema, owned here, parsed by both the
 * API that produces it and the UI that renders it (CLAUDE.md rule 3).
 *
 * This is what makes "every surface updates in unison" true at the boundary as well as in
 * the engine: the dashboard badge, the checklist, the candidate view and the CSV export
 * are all projections of ONE of these, and none of them recomputes a status, a due date or
 * a progress figure.
 *
 * The item carries its own display metadata — label, kind, owner — resolved server-side
 * from the catalog. The client therefore never looks a requirement up by key, which is
 * what keeps `scripts/check-requirement-keys.mjs` satisfiable in feature code.
 */
export const checklistItemSchema = z.object({
    compliance: complianceRuleSchema.optional(),
    requirementKey: requirementKeySchema,
    /** Decision 8: the plain HR-facing name. The legal name is metadata and is not sent. */
    label: z.string(),
    description: z.string(),
    kind: requirementKindSchema,
    owner: requirementOwnerSchema,
    phase: requirementPhaseSchema,
    status: requirementStatusSchema,
    applicability: applicabilitySchema,
    /** Empty means this item blocks nothing — decision 6's agency rows. */
    gates: z.array(gateSchema),
    /** Null when the policy sets no interval, which is not the same as overdue. */
    dueAt: z.string().datetime().nullable(),
    /**
     * How the item relates to time. Without this a one-time form and an event-triggered
     * record are byte-identical on the wire, and separation paperwork renders as an open
     * task on every active employee forever.
     */
    timing: z.enum(checklistTimings),
    /**
     * True when the expiry is printed on the document, so the screen must ask for it and the
     * server will refuse a blank. `timing: 'dated'` cannot answer this — an interval row is
     * dated too and its date comes from the rule.
     *
     * Defaulted, like `directlyRecordable` below, and for the same reason: the web and the API
     * deploy as parallel jobs, and a new bundle parsing a response from the OLD Functions app
     * would otherwise throw on every item and blank every person page until the API caught
     * up — unbounded, if that deploy failed. `buildPersonChecklist` always supplies the value,
     * so nothing in this repo ever relies on the default; it exists for the response that was
     * written before this field was.
     */
    expiryFromDocument: z.boolean().default(false),
    /** Set when the interval was never settled; names the open question. */
    openQuestionId: z.string().nullable(),
    countsTowardProgress: z.boolean(),
    waivable: z.boolean(),
    issues: z.array(reviewCauseSchema),
    /** Which profile or tag put this row in scope — renders as "why is this here?". */
    sources: z.array(z.string()),
    /** I-9 and health rows route to segregated folders and are marked in the UI. */
    segregated: z.boolean(),
    /**
     * Where the person's own work on this row has got to.
     *
     * Distinct from `status`, which is the readiness verdict. A row can be `not_started` as a
     * verdict while the step behind it says `submitted` — the person has done their part and
     * is waiting on HR, and showing them "not started" for that is both wrong and the fastest
     * way to generate a phone call. Null when no step is attached to the requirement.
     */
    stepState: z
        .enum(['not_started', 'sent', 'in_progress', 'submitted', 'approved', 'rejected', 'cancelled'])
        .nullable()
        .default(null),
    /**
     * Whether HR can close this row themselves (#85).
     *
     * Derived here from the rule rather than decided in the UI, for the same reason as
     * `segregated`: the screen must not have to know what a requirement KIND is in order to
     * render the right control. Eight blocking requirements had no path to satisfaction at
     * all until this existed — orientation being the clearest, since HR runs it in a room and
     * the candidate has nothing to submit.
     */
    directlyRecordable: z.boolean().default(false),
    /**
     * The step whose "not ready" upload answer is waiting for HR to send a secure upload link
     * (#232), or null. An opaque instance id, so HR's one click names the step to reopen without
     * the screen deciding anything. Optional, so an older payload and every existing fixture
     * parse unchanged; absent and null both mean "nothing waiting".
     */
    awaitingUploadLinkInstanceId: z.string().min(1).nullable().optional(),
    competencyForms: z.array(z.object({
        jobProfileKey: z.string(),
        assessmentType: z.enum(['initial', 'ongoing']),
        formId: z.string(),
    })).nullable().optional(),
});
export const personChecklistSchema = z.object({
    clearances: z.array(z.object({
        gate: gateSchema, state: readinessStateSchema, applicable: z.boolean(),
        blockers: z.array(z.object({ requirementKey: requirementKeySchema, status: requirementStatusSchema })),
        reviewItems: z.array(z.object({ requirementKey: requirementKeySchema, cause: reviewCauseSchema })),
    })).optional(),
    personId: personIdSchema,
    gate: gateSchema,
    state: readinessStateSchema,
    /** The `now` the answer was derived at, so a screenshot can be reproduced. */
    computedAt: z.string().datetime(),
    /**
     * Counted over what the person can actually act on. An excepted requirement counts as
     * UNMET, so a waiver cannot inflate the figure HR reports to a surveyor.
     */
    progress: z.object({
        met: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        /**
         * How many of `total` can never be met because the policy never set an interval.
         * Sent so the UI can disclose the ceiling instead of showing a percentage that
         * structurally cannot reach 100 — a compliant nurse tops out near 86%.
         */
        unscorable: z.number().int().nonnegative(),
        inScope: z.number().int().nonnegative(),
    }),
    blockers: z.array(z.object({ requirementKey: requirementKeySchema, status: requirementStatusSchema })),
    reviewItems: z.array(z.object({ requirementKey: requirementKeySchema, cause: reviewCauseSchema })),
    appliedExceptions: z.array(z.object({
        requirementKey: requirementKeySchema,
        exceptionId: z.string(),
        reason: z.string(),
        grantedBy: z.string(),
        expiresAt: z.string().datetime().nullable(),
    })),
    /** Already filtered to the requesting surface by the shared visibility predicate. */
    items: z.array(checklistItemSchema),
});
//# sourceMappingURL=wire.js.map